import { SeoHead } from '@/components/SeoHead';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import React, { useState, useRef, useEffect } from "react";
import { 
  Smartphone, Tablet, Download, Star, Award, Zap, Shield, Code, 
  ChevronDown, MessageSquare, Briefcase, HeartPulse, ShoppingCart, 
  Building2, ArrowRight, Check, SmartphoneCharging, Cpu, GitBranch, 
  Layers, Server, Code2, Smartphone as SmartphoneTouch, 
  Users, Clock, BarChart2, Globe, TabletSmartphone, SmartphoneNfc,
  Headphones, Wifi, Battery, Cpu as CpuIcon, HardDrive, Watch as Smartwatch, PowerOff as SmartphoneOff
} from "lucide-react";

// Import app showcase icons
import HealthAppIcon from "@/components/app-showcase/HealthAppIcon";
import FoodAppIcon from "@/components/app-showcase/FoodAppIcon";
import FinanceAppIcon from "@/components/app-showcase/FinanceAppIcon";

// Brand colors for app showcase
const brandColors = [
  '#6366F1', // Indigo
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#F59E0B', // Amber
  '#06B6D4'  // Cyan
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
};

function MobileAppDevelopment() {
  const { openDrawer } = useQuoteDrawer();

  const features = [
    {
      title: "Native iOS & Android",
      description: "High-performance apps built with Swift, Kotlin, and Java for optimal performance.",
      icon: <SmartphoneCharging className="h-8 w-8" />,
      color: "#3B82F6"
    },
    {
      title: "Cross-Platform",
      description: "Single codebase solutions using React Native and Flutter for cost efficiency.",
      icon: <Layers className="h-8 w-8" />,
      color: "#8B5CF6"
    },
    {
      title: "UI/UX Design",
      description: "Beautiful, intuitive interfaces that drive engagement and retention.",
      icon: <SmartphoneTouch className="h-8 w-8" />,
      color: "#EC4899"
    },
    {
      title: "Backend Development",
      description: "Scalable cloud infrastructure and APIs to power your mobile apps.",
      icon: <Server className="h-8 w-8" />,
      color: "#10B981"
    },
    {
      title: "App Store Optimization",
      description: "Maximize your app's visibility and conversion rates in app stores.",
      icon: <Download className="h-8 w-8" />,
      color: "#F59E0B"
    },
    {
      title: "Maintenance & Support",
      description: "Ongoing updates, bug fixes, and feature enhancements.",
      icon: <Code2 className="h-8 w-8" />,
      color: "#6366F1"
    }
  ];

  const technologies = [
    { name: "React Native", icon: <i className="fab fa-react text-4xl text-[#61DAFB]"></i>, color: "#61DAFB" },
    { name: "Flutter", icon: <i className="fas fa-mobile-alt text-4xl text-[#02569B]"></i>, color: "#02569B" },
    { name: "Swift", icon: <i className="fab fa-apple text-4xl text-gray-900"></i>, color: "#F05138" },
    { name: "Kotlin", icon: <i className="fab fa-android text-4xl text-[#3DDC84]"></i>, color: "#3DDC84" },
    { name: "Firebase", icon: <i className="fas fa-fire text-4xl text-[#FFCA28]"></i>, color: "#FFCA28" },
    { name: "AWS Amplify", icon: <i className="fab fa-aws text-4xl text-[#FF9900]"></i>, color: "#FF9900" },
    { name: "GraphQL", icon: <i className="fas fa-bolt text-4xl text-[#E10098]"></i>, color: "#E10098" },
    { name: "Redux", icon: <i className="fas fa-code-branch text-4xl text-[#764ABC]"></i>, color: "#764ABC" }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="h-8 w-8" />,
      title: "Award-Winning Apps",
      description: "Recognized for excellence in mobile app development and design"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "High Performance",
      description: "Optimized apps that run smoothly on all devices"
    },
    {
      icon: <Code className="h-8 w-8" />,
      title: "Clean Code",
      description: "Maintainable and scalable codebase following best practices"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Security First",
      description: "Enterprise-grade security for your app and user data"
    }
  ];

  const industries = [
    {
      name: "Healthcare",
      icon: <HeartPulse className="h-6 w-6" />,
      description: "HIPAA compliant healthcare solutions"
    },
    {
      name: "E-commerce",
      icon: <ShoppingCart className="h-6 w-6" />,
      description: "Mobile shopping experiences"
    },
    {
      name: "Enterprise",
      icon: <Building2 className="h-6 w-6" />,
      description: "Business productivity apps"
    },
    {
      name: "Startups",
      icon: <Zap className="h-6 w-6" />,
      description: "MVP development & scaling"
    }
  ];

  const testimonials = [
    {
      quote: "The app they developed for our clinic has transformed our patient management system. Highly recommended!",
      author: "Dr. Sarah Al Mansoori",
      role: "Medical Director, HealthPlus Clinic"
    },
    {
      quote: "Our e-commerce app saw a 200% increase in mobile orders after the redesign. The team is exceptional!",
      author: "Khalid Al Farsi",
      role: "CEO, UrbanStyle"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to develop a mobile app?",
      answer: "Most apps take 3-6 months from concept to launch, depending on complexity and features."
    },
    {
      question: "Do you provide app maintenance after launch?",
      answer: "Yes, we offer comprehensive maintenance and update packages to keep your app running smoothly."
    },
    {
      question: "What's the difference between native and cross-platform apps?",
      answer: "Native apps are built specifically for one platform (iOS or Android) while cross-platform apps work on both using a single codebase."
    }
  ];
  
  const [activeApp, setActiveApp] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  
  // App showcase data with proper typing
  interface AppShowcaseItem {
    name: string;
    category: string;
    description: string;
    features: string[];
    color: string;
    stats: { value: string; label: string }[];
    icon: React.ReactNode; // React component or element for the app icon
  }

  const appShowcase: AppShowcaseItem[] = [
    {
      name: "HealthTrack Pro",
      category: "Health & Wellness",
      description: "Comprehensive health monitoring with AI-powered insights and personalized recommendations.",
      features: ["Workout Plans", "Meal Tracking", "Health Analytics", "Doctor Connect"],
      color: brandColors[0],
      stats: [
        { value: "4.9", label: "App Store Rating" },
        { value: "500K+", label: "Downloads" },
        { value: "98%", label: "User Retention" }
      ],
      icon: <HealthAppIcon className="w-24 h-24 text-white" />
    },
    {
      name: "Foodie Express",
      category: "Food Delivery",
      description: "Fast food delivery from your favorite restaurants with real-time order tracking.",
      features: ["Live Order Tracking", "Instant Checkout", "Exclusive Deals", "Ratings & Reviews"],
      color: brandColors[2],
      stats: [
        { value: "4.8", label: "Customer Rating" },
        { value: "1M+", label: "Orders Delivered" },
        { value: "15min", label: "Avg. Delivery" }
      ],
      icon: <FoodAppIcon className="w-24 h-24 text-white" />
    },
    {
      name: "WealthGuard",
      category: "Fintech",
      description: "All-in-one financial management with investment tracking and bill payments.",
      features: ["Expense Analytics", "Investment Portfolio", "Bill Reminders", "Credit Score"],
      color: brandColors[4],
      stats: [
        { value: "4.7", label: "User Rating" },
        { value: "250K+", label: "Active Users" },
        { value: "$100M+", label: "Assets Managed" }
      ],
      icon: <FinanceAppIcon className="w-24 h-24 text-white" />
    }
  ];

  // Auto-rotate app showcase
  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(() => {
        setActiveApp((prev) => (prev + 1) % appShowcase.length);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovered, appShowcase.length]);

  return (
    <>
      <SeoHead 
        title="Mobile App Development UAE | ZeeX Soft"
        description="Professional iOS & Android app development in UAE. Custom mobile solutions for businesses since 2017 with 100+ apps delivered."
        keywords="mobile app development uae, ios android dubai, custom apps sharjah"
      />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-32 pb-20 md:pb-32">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/80"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-6"
              >
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
                <span className="text-sm font-medium text-white/80">Transforming Ideas Into Mobile Experiences</span>
              </motion.div>

              <motion.h1 
                className="font-orbitron font-bold text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-white via-gray-100 to-gray-300 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Mobile Apps That <span className="gradient-text">Drive Results</span>
              </motion.h1>
              
              <motion.p 
                className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto lg:mx-0 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                We create beautiful, high-performance mobile applications that captivate users and deliver measurable business impact. From concept to launch and beyond.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                <Button 
                  onClick={(e) => {
                    e.preventDefault();
                    openDrawer('Mobile App Development');
                  }}
                  className="btn-primary group px-8 py-6 text-lg font-medium rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Link href="/portfolio">
                  <Button 
                    variant="outline" 
                    className="bg-transparent border-2 border-white/20 hover:bg-white/10 text-white hover:text-white px-8 py-6 text-lg font-medium rounded-xl transition-all duration-300"
                  >
                    View Our Work
                  </Button>
                </Link>
              </motion.div>

              <motion.div 
                className="mt-12 flex flex-wrap justify-center lg:justify-start gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                {["App Store", "Google Play", "Huawei AppGallery"].map((store, i) => (
                  <div key={i} className="flex items-center bg-white/5 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10">
                    <div className="bg-white/10 p-2 rounded-lg mr-3">
                      {i === 0 ? (
                        <i className="fab fa-apple text-xl"></i>
                      ) : i === 1 ? (
                        <i className="fab fa-google-play text-xl"></i>
                      ) : (
                        <i className="fas fa-mobile-alt text-xl"></i>
                      )}
                    </div>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">Available on</div>
                      <div className="font-medium text-white">{store}</div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Phone Mockup */}
            <motion.div 
              className="relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full filter blur-3xl -z-10"></div>
              <div className="relative">
                <div className="absolute -top-10 -left-10 w-80 h-80 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-full filter blur-2xl -z-10"></div>
                <div className="relative mx-auto w-72 h-[580px] bg-gray-900 rounded-[40px] border-[14px] border-gray-800 overflow-hidden shadow-2xl">
                  {/* Phone screen content */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800 p-6">
                    <div className="flex justify-between items-center mb-8">
                      <div className="text-white font-bold text-xl">ZeeX</div>
                      <div className="text-xs text-gray-400">9:41 AM</div>
                    </div>
                    
                    <div className="space-y-6">
                      <div className="bg-white/5 rounded-2xl p-4 backdrop-blur-sm">
                        <div className="h-3 bg-white/20 rounded-full w-3/4 mb-2"></div>
                        <div className="h-2 bg-white/10 rounded-full w-1/2"></div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-primary to-secondary p-6 rounded-2xl text-white">
                        <h3 className="font-bold text-lg mb-2">Your App Here</h3>
                        <p className="text-sm opacity-80 mb-4">Let's build something amazing together</p>
                        <button className="bg-white text-gray-900 text-sm font-medium px-4 py-2 rounded-lg">Get Started</button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="aspect-square bg-white/5 rounded-xl backdrop-blur-sm"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section id="portfolio" className="relative py-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/80"></div>
        </div>
        <div className="absolute left-0 right-0 h-24 -mt-24 bg-gradient-to-t from-gray-900 to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link href="/portfolio" className="group inline-block">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-900/50 text-blue-300 mb-4 group-hover:bg-blue-800/70 transition-colors">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                Our Work
                <ArrowRight className="ml-2 h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Featured Mobile Apps</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">Explore our portfolio of successful mobile applications that deliver exceptional user experiences and business results.</p>
          </motion.div>

          <div className="relative max-w-6xl mx-auto">
            {/* App Navigation Dots */}
            <div className="flex justify-center space-x-2 mb-8">
              {appShowcase.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveApp(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeApp ? 'bg-blue-400 w-8' : 'bg-white/20'}`}
                  aria-label={`View ${appShowcase[index].name}`}
                />
              ))}
            </div>

            {/* App Showcase */}
            <div 
              className="relative h-[600px] md:h-[700px] rounded-3xl overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              key={`app-showcase-${activeApp}`}
            >
              <AnimatePresence mode="wait">
                {appShowcase.map((app, index) => (
                  index === activeApp && (
                    <motion.div
                      key={app.name}
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 flex flex-col md:flex-row"
                    >
                      {/* App Mockup */}
                      <div className="relative flex-1 flex items-center justify-center p-8 md:p-12 bg-gradient-to-br from-gray-800 to-gray-900">
                        <div className="absolute inset-0 opacity-30">
                          <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center"></div>
                        </div>
                        <div className="relative w-full max-w-xs">
                          <div className="relative mx-auto w-full h-[600px] bg-gray-900 rounded-[40px] border-[14px] border-gray-800 overflow-hidden shadow-2xl">
                            <div className="absolute inset-0" style={{ backgroundColor: app.color }}>
                              <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70 p-6 flex flex-col">
                                <div className="flex justify-between items-center mb-8">
                                  <div className="flex items-center">
                                    <div className="mr-3">
                                      {app.icon}
                                    </div>
                                    <div className="text-white font-bold text-xl">{app.name.split(' ')[0]}</div>
                                  </div>
                                  <div className="text-xs text-white/70">9:41 AM</div>
                                </div>
                                <div className="flex-1 flex flex-col justify-center">
                                  <h3 className="text-2xl font-bold text-white mb-2">{app.name}</h3>
                                  <p className="text-white/80 text-sm mb-6">{app.description}</p>
                                  <div className="grid grid-cols-3 gap-3 mb-6">
                                    {app.stats.map((stat, i) => (
                                      <div key={i} className="bg-white/10 backdrop-blur-sm p-3 rounded-xl text-center">
                                        <div className="text-xl font-bold text-white">{stat.value}</div>
                                        <div className="text-xs text-white/60">{stat.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <button className="bg-white text-gray-900 font-medium py-2 px-6 rounded-full self-start hover:bg-gray-100 transition-colors">
                                    Learn More
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* App Details */}
                      <div className="flex-1 bg-gray-800 p-8 md:p-12 flex flex-col justify-center">
                        <div className="max-w-md mx-auto w-full">
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full mb-4" style={{ backgroundColor: `${app.color}20`, color: app.color }}>
                            {app.category}
                          </span>
                          <h3 className="text-3xl font-bold text-white mb-4">{app.name}</h3>
                          <p className="text-gray-300 mb-6">{app.description}</p>
                          
                          <div className="space-y-3 mb-8">
                            <h4 className="font-medium text-white">Key Features:</h4>
                            <ul className="space-y-2">
                              {app.features.map((feature, i) => (
                                <li key={i} className="flex items-start">
                                  <Check className="h-5 w-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-300">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="flex flex-wrap gap-3">
                            <Link href="/portfolio">
                              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                View Case Study
                              </Button>
                            </Link>
                            <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-700 hover:border-gray-500">
                              Live Demo
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl font-bold text-white mb-4"
            >
              Comprehensive Mobile App Solutions
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-gray-400 max-w-2xl mx-auto"
            >
              From concept to launch and beyond, we provide end-to-end mobile app development services tailored to your business needs.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                custom={index}
              >
                <div className="bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl p-8 transition-all duration-300 h-full">
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg mb-6" style={{ backgroundColor: `${feature.color}20` }}>
                    {React.cloneElement(feature.icon, { className: `h-8 w-8`, style: { color: feature.color } })}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Development Process */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">App Development Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From idea to app store - our proven development methodology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Concept & Strategy", description: "Define app requirements and create development roadmap" },
              { step: "02", title: "UI/UX Design", description: "Create intuitive and engaging user interfaces" },
              { step: "03", title: "Development & Testing", description: "Build and rigorously test your mobile application" },
              { step: "04", title: "App Store Submission", description: "Deploy and optimize for app store success" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-accent to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Why Choose Our Mobile Solutions?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We create mobile experiences that users love and businesses trust
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-accent to-secondary rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                  {item.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our App Showcase</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore some of our successful mobile applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {appShowcase.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect rounded-xl overflow-hidden group hover:shadow-lg transition-shadow duration-300"
              >
                <div className="h-48 bg-gradient-to-r from-accent/10 to-secondary/10 flex items-center justify-center p-4">
                  <div className="h-32 w-32 text-accent">
                    {app.icon}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-orbitron font-semibold text-xl">{app.name}</h3>
                    <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">
                      {app.category}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {app.features.map((feature, i) => (
                      <span key={i} className="px-2 py-1 bg-accent/5 text-accent text-xs rounded">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Industries We Serve</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Custom mobile solutions for diverse business needs
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-6 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center text-accent mb-4">
                  {industry.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{industry.name}</h3>
                <p className="text-muted-foreground text-sm">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Client Success Stories</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="text-yellow-400 mr-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                </div>
                <p className="text-lg italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-accent to-secondary flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold">{testimonial.author}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to know about our mobile app development services
            </p>
          </motion.div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <FAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Mobile Technologies We Use</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Cutting-edge frameworks for powerful mobile applications
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="glass-effect p-6 rounded-xl text-center hover:shadow-lg transition-shadow"
              >
                <i className={`${tech.icon} text-4xl text-accent mb-4`}></i>
                <p className="font-orbitron text-sm">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/10 to-secondary/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/svgs/pattern-grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-6">
              Ready to Build the Next Big App?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's turn your vision into a powerful mobile experience that users will love
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  openDrawer('Mobile App Development');
                }}
                className="btn-primary px-8 py-6 text-lg font-semibold"
                size="lg"
              >
                Start Your App Project
                <Zap className="ml-2 h-5 w-5" />
              </Button>
              <Link href="/contact">
                <Button 
                  className="btn-secondary px-8 py-6 text-lg font-semibold"
                  size="lg"
                >
                  Book a Free Consultation
                  <MessageSquare className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              No obligation. No commitment. Just a friendly chat about your app idea.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default MobileAppDevelopment;
